import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import Avatar from "@/components/Avatar";
import { supabase } from "@/lib/supabase-client";

type UserProfile = {
  id: string;
  preferred_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type PreviewFooterProps = {
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  createdBy?: string | null;
  updatedBy?: string | null;
};

// Simple cache for profile lookups
const profileCache = new Map<string, UserProfile>();

async function fetchProfile(userId: string): Promise<UserProfile | null> {
  if (profileCache.has(userId)) {
    return profileCache.get(userId)!;
  }

  const { data } = await supabase
    .from("profiles")
    .select("id, preferred_name, first_name, last_name")
    .eq("id", userId)
    .maybeSingle();

  if (data) {
    profileCache.set(userId, data);
    return data;
  }
  return null;
}

function formatUserName(profile: UserProfile | null): string {
  if (!profile) return "";
  if (profile.preferred_name) return profile.preferred_name;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
  return fullName || "";
}

export function PreviewFooter({ createdAt, updatedAt, createdBy, updatedBy }: PreviewFooterProps) {
  const { colors } = useTheme();
  const { formatTimestamp } = useDateTime();
  const [createdByProfile, setCreatedByProfile] = useState<UserProfile | null>(null);
  const [updatedByProfile, setUpdatedByProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (createdBy) {
      fetchProfile(createdBy).then(setCreatedByProfile);
    }
    if (updatedBy) {
      fetchProfile(updatedBy).then(setUpdatedByProfile);
    }
  }, [createdBy, updatedBy]);

  const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return "—";
    const d = typeof date === "string" ? new Date(date) : date;
    return formatTimestamp(d, true);
  };

  // Don't render if no audit data
  if (!createdAt && !updatedAt) return null;

  const createdByName = formatUserName(createdByProfile);
  const updatedByName = formatUserName(updatedByProfile);

  return (
    <View style={[styles.container, { borderColor: colors.border, backgroundColor: colors.background }]}>
      {createdAt ? (
        <View style={styles.item}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Created</ThemedText>
          <ThemedText style={styles.value}>{formatDate(createdAt)}</ThemedText>
          {createdBy ? (
            <View style={styles.userInfo}>
              <Avatar userId={createdBy} size={16} fontSize={8} />
              {createdByName ? (
                <ThemedText style={[styles.userName, { color: colors.textSecondary }]}>{createdByName}</ThemedText>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}
      {updatedAt ? (
        <View style={styles.item}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Updated</ThemedText>
          <ThemedText style={styles.value}>{formatDate(updatedAt)}</ThemedText>
          {updatedBy ? (
            <View style={styles.userInfo}>
              <Avatar userId={updatedBy} size={16} fontSize={8} />
              {updatedByName ? (
                <ThemedText style={[styles.userName, { color: colors.textSecondary }]}>{updatedByName}</ThemedText>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingVertical: 6,
    gap: 24,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  label: {
    fontSize: 10,
    textTransform: "uppercase",
  },
  value: {
    fontSize: 11,
  },
  userInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  userName: {
    fontSize: 10,
  },
});
